import { Row, Col, CardBody, Card, Button, Modal, Alert } from "react-bootstrap";
import useMyDisplayName from "../shared/useMyDisplayName";
import crudApi from "@serie3/common/api/crudApi";
import { useEffect, useState, useContext, useMemo, useRef } from "react";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import { canAccessAllPlatforms } from "@serie3/common/config/RoleRights";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import PostFormView from "./components/PostFormView";
import ToastMessages from "../../ToastMessages";
import { useTranslation } from "react-i18next";
import LoadingModal from "../shared/Modals/LoadingModal";
import VariantsModal from "./components/VariantsModal";
import TelepromterModal from "./components/TelepromterModal";
import MegaprompterModal from "./components/Megaprompter/MegaprompterModal";
import ShareModal from "./components/ShareModal";
import AddBtn from "./components/Timeline/AddBtn";
import { sortPostsByType } from "@serie3/mymediaplanner/Modules/Timeline/Domain/sortPosts";
import PostAssistant from "./components/PostAssistant";
import GenericKanbanBoard from "../shared/KanbanBoard/GenericKanbanBoard";
import TimelinePostMenu from "./components/Timeline/TimelinePostMenu";
import NewModal from "../shared/Modals/NewModal";
import PostPreviewPanel from "./components/Previews/PostPreviewPanel";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import { currentUsername, delegatedScheduleFor, personName } from "@serie3/mymediaplanner/Modules/Social/Domain/delegation";
import { ApprovalBadge } from "./components/Approvals/approvalStyles";
import WithdrawApprovalButton, { canWithdraw } from "./components/Approvals/WithdrawApprovalButton";
import { DecideButtons } from "./components/Approvals/ApprovalScheduleList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRectangleList, faPaperPlane, faVideo } from "@fortawesome/free-solid-svg-icons";
import { faLinkedin, faYoutube, faMeta, faTiktok } from "@fortawesome/free-brands-svg-icons";
import YoutubeIcon from "../shared/YoutubeIcon";

const PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: faLinkedin, color: "#0077b5" },
  { key: "youtube",  label: "YouTube",  icon: faYoutube,  color: "#ff0000" },
  { key: "meta",     label: "Meta",     icon: faMeta,     color: "#0081fb" },
  { key: "tiktok",   label: "TikTok",   icon: faTiktok,   color: "#010101" },
];

const PublishModal = ({ show, onHide, onConfirm, allPlatforms = false }) => {
  const [selected, setSelected] = useState(["linkedin"]);
  const { t } = useTranslation();

  const visiblePlatforms = allPlatforms
    ? PLATFORMS
    : PLATFORMS.filter((p) => p.key === "linkedin");

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered onShow={() => setSelected(["linkedin"])}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          {t("Backend.Post.Publish")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
          {t("Backend.Post.SelectPlatforms", "Select platforms to publish to")}
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {visiblePlatforms.map(({ key, label, icon, color }) => {
            const isSelected = selected.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(key)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  gap: 6, padding: "12px 18px", borderRadius: 12, cursor: "pointer",
                  border: isSelected ? `2px solid ${color}` : "2px solid #e0e4ea",
                  background: isSelected ? `${color}18` : "#fff",
                  color: isSelected ? color : "#9299a8",
                  transition: "all 0.15s ease", minWidth: 85,
                }}
              >
                {key === "youtube"
                  ? <YoutubeIcon height={24} />
                  : <FontAwesomeIcon icon={icon} style={{ fontSize: 24 }} />}
                <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
              </button>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} style={{ borderRadius: 8, fontSize: 13 }}>
          {t("Backend.Buttons.Cancel", "Cancel")}
        </Button>
        <Button
          onClick={() => onConfirm(selected)}
          disabled={selected.length === 0}
          style={{ borderRadius: 8, fontSize: 13, background: "#038edc", border: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
          {t("Backend.Post.Publish")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ForceDeleteModal = ({ show, details, onConfirm, onHide }) => {
  const { t } = useTranslation();
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          {t("Backend.Post.ForceDelete.Title", "Delete post anyway?")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" style={{ fontSize: 13, marginBottom: 0 }}>
          {details || t("Backend.Post.ForceDelete.DefaultDetails", "This post is still referenced by other records.")}
        </Alert>
        <p style={{ marginTop: 12, fontSize: 13, color: "#374151" }}>
          {t("Backend.Post.ForceDelete.Confirm", "All connected records (e.g. group shares and comments) will also be deleted. This cannot be undone.")}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} style={{ borderRadius: 8, fontSize: 13 }}>
          {t("Backend.Buttons.Cancel", "Cancel")}
        </Button>
        <Button variant="danger" onClick={onConfirm} style={{ borderRadius: 8, fontSize: 13 }}>
          {t("Backend.Post.ForceDelete.Button", "Delete anyway")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/** "planned by X" plus the approval state, on a card someone else planned on my account. */
const DelegatedTag = ({ schedule }) => {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
      <span style={{ fontSize: 11, color: "#74788d", whiteSpace: "nowrap" }}>
        {t("Backend.Timeline.PlannedBy", "planned by")} <strong style={{ color: "#1a2236" }}>{personName(schedule.planned_by)}</strong>
      </span>
      <ApprovalBadge status={schedule.approval_status} title={schedule.approval_comment} />
    </div>
  );
};

export default function TimelineKanbanWrapper() {
  const { settings } = useContext(SettingsContext);
  const allPlatforms = canAccessAllPlatforms(settings?.user?.roles);
  const myUsername = currentUsername(settings);
  // My display name for the author line of my own cards (same source as the header).
  const myName = useMyDisplayName();
  // Read-only view of a post someone else wrote and planned on my account.
  const [previewPost, setPreviewPost] = useState(null);
  const [previewFiles, setPreviewFiles] = useState([]);

  const [previewBinary, setPreviewBinary] = useState(undefined);
  const openDelegatedPreview = async (post) => {
    setPreviewFiles([]);
    setPreviewPost(post);
    // Someone else's post: files and bytes through the member-gated group
    // routes (the document routes are owner-only).
    const groupId = Array.isArray(post?.shared_in) && post.shared_in[0]?.id ? post.shared_in[0].id : null;
    setPreviewBinary(groupId ? MediaplannerGroupEndpoints.endpoints.getImageBinary + groupId + "/" : undefined);
    const r = await crudApi.getAll(
      groupId
        ? MediaplannerGroupEndpoints.endpoints.postFiles + groupId + "/" + post.id
        : DocumentsEndpoints.showPostFiles + post.id
    );
    if (r?.ok) setPreviewFiles(Array.isArray(r.data) ? r.data : []);
  };
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("unpublished");

  // FEATURE UI STATE (stays in wrapper)
  const [showEdit, setShowEdit] = useState(false);
  const [editPost, setEditPost] = useState();
  const [showTeleprompter, setShowTelepromter] = useState(false);
  const [showMegaprompter, setShowMegaprompter] = useState(false);
  const [trigger, setTrigger] = useState(-1);
  const [showVariants, setShowVariants] = useState(false);
  const [showPosting, setShowPosting] = useState(false);
  const [showGenerating, setShowGenerating] = useState(false);
  const [showPostAssistant, setShowPostAssistant] = useState(false);
  const [activeKey, setActiveKey] = useState("content");
  const [showShare, setShowShare] = useState(false);
  const [sharePostId, setSharePostId] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishPostId, setPublishPostId] = useState(null);
  const [showForceDelete, setShowForceDelete] = useState(false);
  const [forceDeletePostId, setForceDeletePostId] = useState(null);
  const [forceDeleteDetails, setForceDeleteDetails] = useState(null);

  const { t } = useTranslation();

  // Wrapper owns columns state (because we want optimistic UI updates on drag)
  const [columns, setColumns] = useState({
    drafts: { name: "Drafts", tasks: [] },
    in_progress: { name: "In Progress", tasks: [] },
    ready: { name: "Ready", tasks: [] },
    planned: { name: "Planned", tasks: [] },
    published: { name: "Published", tasks: [] },
  });

  const [tagFilter, setTagFilter] = useState("");

  const filteredColumns = useMemo(() => {
    const query = tagFilter.trim().toLowerCase();
    if (!query) return columns;
    const matches = (task) => {
      const post = task?.post ?? task;
      const searchable = [
        ...(post?.tags ?? []).map((t) => t.name?.toLowerCase()),
        post?.post_type?.toLowerCase(),
        post?.content_type?.toLowerCase(),
        post?.content_pillar?.pillar?.toLowerCase(),
      ].filter(Boolean);
      return searchable.some((s) => s.includes(query));
    };
    return Object.fromEntries(
      Object.entries(columns).map(([colId, col]) => [
        colId,
        { ...col, tasks: (col.tasks ?? []).filter(matches) },
      ])
    );
  }, [columns, tagFilter]);

  // Posts currently visible on the board, used by the Megaprompter picker.
  // Each post keeps the column it came from so the picker can filter by it.
  const megaprompterPosts = useMemo(() => {
    const visibleColumns = viewMode === "published"
      ? { published: filteredColumns.published }
      : Object.fromEntries(Object.entries(filteredColumns).filter(([k]) => k !== "published"));
    const out = [];
    for (const [columnId, col] of Object.entries(visibleColumns ?? {})) {
      for (const task of col?.tasks ?? []) {
        const post = task?.post ?? task;
        if (post && post.text) {
          // Group by content type since the Timeline isn't scoped to one social group.
          out.push({
            ...post,
            __groupName: post.content_type || "Posts",
            __columnId: columnId,
            __columnName: col?.name ?? columnId,
          });
        }
      }
    }
    return out;
  }, [filteredColumns, viewMode]);

  const applyColumnsFromPosts = (postsList) => {
    const sorted = sortPostsByType(postsList);
    const safeTasks = (colId) => sorted?.[colId]?.tasks ?? [];

    setColumns((prev) => ({
      ...prev,
      drafts: { ...prev.drafts, tasks: safeTasks("drafts") },
      in_progress: { ...prev.in_progress, tasks: safeTasks("in_progress") },
      ready: { ...prev.ready, tasks: safeTasks("ready") },
      planned: { ...prev.planned, tasks: safeTasks("planned") },
      published: { ...prev.published, tasks: safeTasks("published") },
    }));
  };

  // FETCHING belongs in wrapper
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const endpoint = viewMode === "published"
        ? SocialEndpoints.posts.publishedOnly
        : SocialEndpoints.posts.unpublished;
      await crudApi.getAll(endpoint, setPosts, SocialEndpoints.posts.dataField);
      setLoading(false);
    };
    fetchPosts();
  }, [trigger, viewMode]);

  // Derive columns from posts whenever posts changes (wrapper concern)
  useEffect(() => {
    if (posts?.length) applyColumnsFromPosts(posts);
    else {
      setColumns((prev) => ({
        ...prev,
        drafts: { ...prev.drafts, tasks: [] },
        in_progress: { ...prev.in_progress, tasks: [] },
        ready: { ...prev.ready, tasks: [] },
        planned: { ...prev.planned, tasks: [] },
        published: { ...prev.published, tasks: [] },
      }));
    }
  }, [posts]);

  // ACTIONS belong in wrapper
  const editAction = (post) => {
    if (delegatedScheduleFor(post, myUsername)) {
      openDelegatedPreview(post);
      return;
    }
    setEditPost(post);
    setShowEdit(true);
  };

  const deletePost = async (postId) => {
    try {
      const response = await crudApi.del(SocialEndpoints.posts.delete, postId);
      if (response.status === 409) {
        setForceDeletePostId(postId);
        setForceDeleteDetails(response.data?.details ?? response.data?.error ?? null);
        setShowForceDelete(true);
        return;
      }
      if (!response.ok) {
        ToastMessages.Error("Error deleting post");
        return;
      }
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      ToastMessages.SuccessfulSave(t("Backend.Post.SuccessfulDeleted"));
    } catch (e) {
      console.error(e);
      ToastMessages.Error("Network error deleting post");
    }
  };

  const forceDeletePost = async () => {
    setShowForceDelete(false);
    try {
      const response = await crudApi.delForce(SocialEndpoints.posts.delete, forceDeletePostId);
      if (!response.ok) {
        ToastMessages.Error("Error deleting post");
        return;
      }
      setPosts((prev) => prev.filter((p) => p.id !== forceDeletePostId));
      ToastMessages.SuccessfulSave(t("Backend.Post.SuccessfulDeleted"));
    } catch (e) {
      console.error(e);
      ToastMessages.Error("Network error deleting post");
    }
  };

  /** The post as the API now sees it, swapped into the board. */
  const reloadPost = async (postId) => {
    const response = await crudApi.justGet(SocialEndpoints.posts.show + postId);
    if (response.ok && response.data?.id) {
      setPosts((prev) => prev.map((p) => (p.id === response.data.id ? response.data : p)));
    }
  };

  const publish = async (postId, platforms) => {
    setShowPublishModal(false);
    setShowPosting(true);
    try {
      const response = await crudApi.createNew(
        SocialEndpoints.posts.publish + postId,
        { platforms },
      );

      // publish answers with per-platform results ({linkedin: "ok"}), not the
      // post; read it back so the card moves (published, or error on a failure)
      await reloadPost(postId);

      if (!response.ok) {
        ToastMessages.Error(t("Backend.Post.ErrorPublishing"));
        setShowPosting(false);
        return;
      }

      ToastMessages.SuccessfulSave(t("Backend.Post.SuccessfulPublished"));
    } catch (e) {
      console.error(e);
      ToastMessages.Error(t("Backend.Post.ErrorPublishing"));
    }
    setShowPosting(false);
  };

  const telepromterAction = (post) => {
    setShowTelepromter(true);
    setEditPost(post);
  };

  const shareAction = (postId) => {
    setShowShare(true);
    setSharePostId(postId);
  };

  const updatePostStatus = async (post, columnId) => {
    if (delegatedScheduleFor(post, myUsername)) {
      // Not my post: the column follows the author's schedule. Snap back.
      applyColumnsFromPosts(posts);
      return;
    }
    let url;

    switch (columnId) {
      case "drafts":
        url = SocialEndpoints.posts.setDraft + post.id;
        break;
      case "in_progress":
        url = SocialEndpoints.posts.markInProgress + post.id;
        break;
      case "ready":
        url = SocialEndpoints.posts.setReady + post.id;
        break;
      case "published":
        url = SocialEndpoints.posts.setReady + post.id;
        break;
      default:
        return;
    }

    const response = await crudApi.justGet(url);
    if (response.ok && response.data?.id) {
      const updatedPost = response.data;
      setPosts((prev) =>
        prev.map((p) => (p.id === updatedPost.id ? updatedPost : p)),
      );
    } else {
      // the optimistic move did not stick: snap the card back
      applyColumnsFromPosts(posts);
      ToastMessages.Error();
    }
  };

  // The board renders <ItemMenu/> per card. A component created anew on every
  // render of this wrapper is a new type to React, so each re-render (a fetch
  // resolving, a name loading) would unmount every menu and close the one the
  // user just opened. Keep one component for the wrapper's lifetime and let it
  // read the current handlers through a ref.
  const menuHandlers = useRef({});
  menuHandlers.current = {
    myUsername,
    deletePost,
    openPublish: (post) => { setPublishPostId(post.id); setShowPublishModal(true); },
    openVariants: (post) => { setShowVariants(true); setEditPost(post); },
    telepromterAction,
    shareAction,
  };
  const ItemMenu = useMemo(
    () =>
      function TimelineItemMenu({ post }) {
        const h = menuHandlers.current;
        const delegated = delegatedScheduleFor(post, h.myUsername);
        if (delegated) return <DelegatedTag schedule={delegated} />;
        return (
          <TimelinePostMenu
            post={post}
            onDelete={() => h.deletePost(post.id)}
            onPublish={() => h.openPublish(post)}
            onVariants={() => h.openVariants(post)}
            onTeleprompter={() => h.telepromterAction(post)}
            onShare={() => h.shareAction(post.id)}
          />
        );
      },
    []
  );
  return (
    <>
      <NewModal
        show={Boolean(previewPost)}
        setShow={(show) => { if (!show) setPreviewPost(null); }}
        title={previewPost?.title}
        size="xl"
        noPadding
        ExtraButton={(() => {
          const d = previewPost ? delegatedScheduleFor(previewPost, myUsername) : null;
          if (!d || d.approval_status !== "proposed") return null;
          return () => (
            <DecideButtons
              key={d.id}
              schedule={d}
              onDecided={(updated) => {
                const patch = (p) => p.id === previewPost.id
                  ? { ...p, schedules: (p.schedules ?? []).map((sch) => (sch.id === updated.id ? { ...sch, ...updated } : sch)) }
                  : p;
                setPosts((prev) => (prev ?? []).map(patch));
                setPreviewPost((prev) => (prev ? patch(prev) : prev));
              }}
            />
          );
        })()}
      >
        {previewPost && (() => {
          const d = delegatedScheduleFor(previewPost, myUsername);
          return (
            <PostPreviewPanel post={previewPost} author={d?.planned_by ?? null} files={previewFiles} binaryEndpoint={previewBinary}>
              {canWithdraw(d, myUsername) && (
                <WithdrawApprovalButton
                  schedule={d}
                  size="sm"
                  style={{ alignSelf: "flex-start" }}
                  onDone={(updated) => setPosts((prev) => (prev ?? []).map((p) =>
                    p.id === previewPost.id
                      ? { ...p, schedules: (p.schedules ?? []).map((sch) => (sch.id === updated.id ? { ...sch, ...updated } : sch)) }
                      : p
                  ))}
                />
              )}
            </PostPreviewPanel>
          );
        })()}
      </NewModal>
      <PostAssistant
        show={showPostAssistant}
        setShow={setShowPostAssistant}
        setShowEdit={setShowEdit}
        posts={posts}
        setPosts={setPosts}
        setEditPost={setEditPost}
        showSpinner={showGenerating}
        setShowSpinner={setShowGenerating}
      />

      <PublishModal
        show={showPublishModal}
        onHide={() => setShowPublishModal(false)}
        onConfirm={(platforms) => publish(publishPostId, platforms)}
        allPlatforms={allPlatforms}
      />

      <ForceDeleteModal
        show={showForceDelete}
        details={forceDeleteDetails}
        onConfirm={forceDeletePost}
        onHide={() => setShowForceDelete(false)}
      />

      <ShareModal
        show={showShare}
        setShow={setShowShare}
        sharePostId={sharePostId}
      />

      <LoadingModal
        showSpinner={showPosting}
        setShowSpinner={setShowPosting}
        text={t("Backend.Post.Publishing")}
      />
      <LoadingModal
        showSpinner={showGenerating}
        setShowSpinner={setShowGenerating}
        text={t("Backend.Post.Assistant.GeneratingPost")}
      />

      <VariantsModal
        show={showVariants}
        setShow={setShowVariants}
        post={editPost}
        dataList={posts}
        setDataList={setPosts}
        activeKey={activeKey}
        setActiveKey={setActiveKey}
      />

      {editPost && (
        <PostFormView
          showEdit={showEdit}
          setShowEdit={setShowEdit}
          dataList={posts}
          setDataList={setPosts}
          editData={editPost}
          setEditData={setEditPost}
          setTrigger={setTrigger}
          trigger={trigger}
        />
      )}

      <TelepromterModal
        post={editPost}
        show={showTeleprompter}
        setShow={setShowTelepromter}
      />
      <MegaprompterModal
        show={showMegaprompter}
        setShow={setShowMegaprompter}
        posts={megaprompterPosts}
        onPostUpdated={(postId, tags) =>
          setPosts((prev) =>
            (prev ?? []).map((p) => (p.id === postId ? { ...p, tags } : p))
          )
        }
      />
      <h4 className="page-section-header">
        <span className="page-section-header-icon" style={{ background: "rgba(3,142,220,0.12)" }}>
          <FontAwesomeIcon icon={faRectangleList} style={{ color: "#038edc" }} />
        </span>
        {t("Backend.Timeline.Posts")}
      </h4>
      <Card className="mt-3">
        <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2" style={{ background: "transparent", borderBottom: "1px solid #eaecf0", padding: "12px 16px" }}>
          <div style={{ display: "flex", gap: 4, background: "#f3f4f6", borderRadius: 8, padding: 3, alignSelf: "flex-start" }}>
            {[
              { key: "unpublished", label: "Unpublished" },
              { key: "published",   label: "Published" },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setViewMode(key)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: "5px 14px", borderRadius: 6,
                  border: "none", cursor: "pointer", transition: "all 0.15s ease",
                  background: viewMode === key ? "#fff" : "transparent",
                  color: viewMode === key ? "#038edc" : "#6b7280",
                  boxShadow: viewMode === key ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-2 justify-content-end">
            <button
              type="button"
              onClick={() => setShowMegaprompter(true)}
              title="Megaprompter — record multiple talking-head videos in a row"
              style={{
                padding: "6px 14px", borderRadius: 50, border: "none",
                background: "linear-gradient(135deg,#038edc,#0277a3)",
                color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
                boxShadow: "0 3px 10px rgba(3,142,220,0.3)",
              }}
            >
              <FontAwesomeIcon icon={faVideo} style={{ fontSize: 12 }} />
              <span className="d-none d-sm-inline">Megaprompter</span>
            </button>
            <AddBtn
              setShowPostAssistant={setShowPostAssistant}
              setEditPost={setEditPost}
              setShowEdit={setShowEdit}
              posts={posts}
              setPosts={setPosts}
            />
          </div>
        </Card.Header>
        <div style={{ padding: "8px 16px", borderBottom: "1px solid #eaecf0", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, flexShrink: 0 }}>Filter by tag:</span>
          <input
            type="text"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            placeholder="e.g. Personal, Video, published…"
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", outline: "none", width: 220 }}
          />
          {tagFilter && (
            <button
              type="button"
              onClick={() => setTagFilter("")}
              style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", background: "transparent", color: "#6b7280", cursor: "pointer" }}
            >
              Clear
            </button>
          )}
        </div>
        <CardBody>

          <Row>
            <Col>
              {loading ? (
                <div style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 8 }}>
                  {[
                  t("Backend.Timeline.Columns.Drafts"),
                  t("Backend.Timeline.Columns.InProgress"),
                  t("Backend.Timeline.Columns.Ready"),
                  t("Backend.Timeline.Columns.Planned"),
                  t("Backend.Timeline.Columns.Published"),
                ].map((col) => (
                    <div key={col} style={{ minWidth: 200, flex: "0 0 200px" }}>
                      <div className="skeleton" style={{ height: 24, width: "70%", borderRadius: 6, marginBottom: 12 }} />
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="skeleton" style={{ height: 80, borderRadius: 10, marginBottom: 10 }} />
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <GenericKanbanBoard
                  columns={viewMode === "published"
                    ? { published: filteredColumns.published }
                    : Object.fromEntries(Object.entries(filteredColumns).filter(([k]) => k !== "published"))
                  }
                  setColumns={setColumns}
                  getId={(task) => task?.id ?? task?.post?.id}
                  getPost={(task) => task?.post ?? task}
                  ItemMenu={ItemMenu}
                  getItemProps={(post) => {
                    const delegated = delegatedScheduleFor(post, myUsername);
                    if (delegated) {
                      // Someone else's post on my account: their avatar, read-only for me.
                      return { readOnly: true, currentUsername: myUsername, author: { id: delegated.planned_by?.id, name: delegated.planned_by?.name, username: delegated.planned_by?.username } };
                    }
                    return { currentUsername: myUsername, author: { id: "me", name: myName, username: myUsername } };
                  }}
                  onEdit={editAction}
                  onStatusChange={async (post, targetColumnId) => {
                    await updatePostStatus(post, targetColumnId);
                  }}
                />
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
}
